import { useMemo, type ComponentType } from "react";
import {
  Clock3,
  TrendingDown,
  TrendingUp,
  Zap,
  type LucideProps,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { COUNTRY_CONFIG } from "@/config/countries";
import { getCurrentTimeSlot, slotTimeLabel } from "@/services/priceApi";
import { cn } from "@/lib/utils";
import { computePriceBreakdown } from "@/utils/priceCalculations";
import { formatEuro } from "@/utils/format";
import { STRINGS } from "@/i18n";

interface PriceSummaryCardsProps {
  data: Record<string, number>;
  interval: "60m" | "15m";
  dateLabel: string;
  providerPerKwhCharge: number;
  providerSpotCoefficient?: number;
  includeTaxes: boolean;
  currentTime: Date;
  regionId?: string | null;
}

interface PricePoint {
  slot: string;
  timeRange: string;
  price: number;
}

interface PriceWindow {
  average: number;
  timeRange: string;
}

interface StatisticCardProps {
  icon: ComponentType<LucideProps>;
  iconClassName: string;
  iconWrapperClassName: string;
  label: string;
  primaryValue: string;
  primaryValueAriaLabel?: string;
  secondaryValue: string;
  className?: string;
}

interface MobilePriceOverviewProps {
  current?: PricePoint;
  cheapest: PricePoint;
  cheapestThreeHourWindow?: PriceWindow;
  isToday: boolean;
  mostExpensive: PricePoint;
  average: number;
  dateLabel: string;
}

const formatTimeRange = (slot: string, interval: "60m" | "15m") => {
  const start = slotTimeLabel(slot);
  if (!start) return slot;

  const [hours, minutes] = start.split(":").map(Number);
  const endMinutes = hours * 60 + minutes + (interval === "15m" ? 15 : 60);
  const end =
    endMinutes >= 24 * 60
      ? "24:00"
      : `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(
          endMinutes % 60
        ).padStart(2, "0")}`;

  return `${start}–${end}`;
};

const formatPrice = (price: number) => formatEuro(price, 3);

// Other days have no current price, so their three cards share one row.
const desktopGridColumns = (showCurrentPrice: boolean) =>
  showCurrentPrice ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3";

const positionInRange = (price: number, minimum: number, maximum: number) => {
  if (minimum === maximum) return 50;
  return ((price - minimum) / (maximum - minimum)) * 100;
};

const findCheapestThreeHourWindow = (
  pricePoints: PricePoint[],
  interval: "60m" | "15m"
): PriceWindow | undefined => {
  const windowSize = interval === "15m" ? 12 : 3;
  if (pricePoints.length < windowSize) return undefined;

  let windowTotal = 0;
  for (let index = 0; index < windowSize; index += 1) {
    windowTotal += pricePoints[index].price;
  }

  let cheapestStartIndex = 0;
  let cheapestTotal = windowTotal;
  for (let index = windowSize; index < pricePoints.length; index += 1) {
    windowTotal +=
      pricePoints[index].price - pricePoints[index - windowSize].price;
    if (windowTotal < cheapestTotal) {
      cheapestTotal = windowTotal;
      cheapestStartIndex = index - windowSize + 1;
    }
  }

  const firstPoint = pricePoints[cheapestStartIndex];
  const lastPoint = pricePoints[cheapestStartIndex + windowSize - 1];
  const start = firstPoint.timeRange.split("–")[0];
  const end = lastPoint.timeRange.split("–")[1];

  return {
    average: cheapestTotal / windowSize,
    timeRange: `${start}–${end}`,
  };
};

function StatisticCard({
  icon: Icon,
  iconClassName,
  iconWrapperClassName,
  label,
  primaryValue,
  primaryValueAriaLabel,
  secondaryValue,
  className,
}: StatisticCardProps) {
  return (
    <Card
      role="group"
      aria-label={label}
      className={cn("min-w-0", className)}
    >
      <CardContent className="flex h-full flex-col p-3 xs:p-4 lg:p-5 xl:p-6">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg xs:h-9 xs:w-9",
              iconWrapperClassName
            )}
          >
            <Icon
              className={cn("h-4 w-4 xs:h-5 xs:w-5", iconClassName)}
              aria-hidden="true"
            />
          </div>
          <h2 className="min-w-0 text-[0.6875rem] font-semibold uppercase leading-tight tracking-wide text-muted-foreground xs:text-xs lg:text-sm">
            {label}
          </h2>
        </div>
        <p
          className="mt-3 whitespace-nowrap text-[clamp(1rem,5vw,1.5rem)] font-bold leading-none tabular-nums lg:text-xl xl:text-2xl"
          aria-label={primaryValueAriaLabel}
        >
          {primaryValue}
        </p>
        <p className="mt-2 text-xs text-muted-foreground xs:text-sm">
          {secondaryValue}
        </p>
      </CardContent>
    </Card>
  );
}

function MobilePriceOverview({
  current,
  cheapest,
  cheapestThreeHourWindow,
  isToday,
  mostExpensive,
  average,
  dateLabel,
}: MobilePriceOverviewProps) {
  const averagePosition = positionInRange(
    average,
    cheapest.price,
    mostExpensive.price
  );
  const currentPosition = current
    ? positionInRange(current.price, cheapest.price, mostExpensive.price)
    : null;
  const primaryLabel = current
    ? STRINGS.summary.currentPrice
    : STRINGS.summary.averagePrice;
  const primaryPrice = current?.price ?? average;
  const primaryContext = current?.timeRange ?? dateLabel;
  const spokenSummary = current
    ? STRINGS.summary.spokenWithCurrent(
        formatPrice(current.price),
        formatPrice(cheapest.price),
        formatPrice(mostExpensive.price),
        formatPrice(average)
      )
    : STRINGS.summary.spokenAverageOnly(
        formatPrice(average),
        formatPrice(cheapest.price),
        formatPrice(mostExpensive.price)
      );

  return (
    <Card className="md:hidden" data-testid="mobile-price-overview">
      <CardContent className="p-0">
        <div className="flex items-start justify-between gap-4 px-4 pb-4 pt-4 xs:px-5 xs:pt-5">
          <div className="min-w-0">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {primaryLabel}
            </h2>
            <p
              className="mt-2 text-3xl font-bold leading-none tabular-nums"
              data-testid="mobile-primary-price"
            >
              {formatPrice(primaryPrice)}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {STRINGS.summary.perKwh}
            </p>
          </div>
          <p className="max-w-32 text-right text-sm font-medium leading-snug text-foreground">
            {primaryContext}
          </p>
        </div>

        {isToday ? (
          <div className="border-t border-border px-5 py-4">
            <p className="text-xs font-medium text-muted-foreground">
              {STRINGS.summary.comparedToToday}
            </p>
            <p className="sr-only">{spokenSummary}</p>
            <div className="relative mt-5 h-12" aria-hidden="true">
              <div className="absolute left-0 right-0 top-5 h-2 rounded-full bg-linear-to-r from-emerald-200 via-muted to-rose-200 dark:from-emerald-900 dark:to-rose-900" />
              <div className="absolute left-0 top-4.5 h-3 w-3 -translate-x-1/2 rounded-full bg-positive" />
              <div className="absolute right-0 top-4.5 h-3 w-3 translate-x-1/2 rounded-full bg-destructive" />
              <div
                className="absolute top-4.25 h-3.5 w-3.5 -translate-x-1/2 rotate-45 border-2 border-card bg-foreground"
                style={{ left: `${averagePosition}%` }}
                data-testid="average-price-marker"
              />
              <span
                className="absolute top-9 -translate-x-1/2 text-[0.625rem] font-medium text-muted-foreground"
                style={{ left: `${averagePosition}%` }}
              >
                {STRINGS.summary.averageShort}
              </span>
              {currentPosition !== null && (
                <>
                  <div
                    className="absolute top-3.5 h-5 w-5 -translate-x-1/2 rounded-full border-4 border-card bg-primary shadow-sm ring-1 ring-primary"
                    style={{ left: `${currentPosition}%` }}
                    data-testid="current-price-marker"
                  />
                  <span
                    className="absolute top-0 -translate-x-1/2 text-[0.625rem] font-semibold text-primary"
                    style={{ left: `${currentPosition}%` }}
                  >
                    {STRINGS.summary.now}
                  </span>
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            className="border-t border-border px-5 py-4"
            data-testid="cheapest-three-hour-window"
          >
            <p className="text-xs font-medium text-muted-foreground">
              {STRINGS.summary.cheapestBlock}
            </p>
            {cheapestThreeHourWindow ? (
              <div className="mt-2 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xl font-bold tabular-nums text-foreground">
                    {cheapestThreeHourWindow.timeRange}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {STRINGS.summary.blockAverage(
                      formatPrice(cheapestThreeHourWindow.average)
                    )}
                  </p>
                </div>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-positive/10 text-positive">
                  <TrendingDown className="h-5 w-5" aria-hidden="true" />
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                {STRINGS.summary.notEnoughData}
              </p>
            )}
          </div>
        )}

        <dl className="grid grid-cols-3 border-t border-border">
          <div className="min-w-0 px-3 py-3 text-left xs:px-4">
            <dt className="text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground xs:text-xs">
              {STRINGS.summary.lowest}
            </dt>
            <dd className="mt-1 text-sm font-bold tabular-nums xs:text-base">
              {formatPrice(cheapest.price)}
            </dd>
            <dd className="mt-1 whitespace-nowrap text-xs text-muted-foreground xs:text-sm">
              {cheapest.timeRange}
            </dd>
          </div>
          <div className="min-w-0 border-x border-border px-2 py-3 text-center xs:px-4">
            <dt className="text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground xs:text-xs">
              {STRINGS.summary.average}
            </dt>
            <dd className="mt-1 text-sm font-bold tabular-nums xs:text-base">
              {formatPrice(average)}
            </dd>
            <dd className="mt-1 text-xs text-muted-foreground xs:text-sm">
              {isToday ? STRINGS.today : STRINGS.summary.dayPrice}
            </dd>
          </div>
          <div className="min-w-0 px-3 py-3 text-right xs:px-4">
            <dt className="text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground xs:text-xs">
              {STRINGS.summary.highest}
            </dt>
            <dd className="mt-1 text-sm font-bold tabular-nums xs:text-base">
              {formatPrice(mostExpensive.price)}
            </dd>
            <dd className="mt-1 whitespace-nowrap text-xs text-muted-foreground xs:text-sm">
              {mostExpensive.timeRange}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

export function PriceSummaryCards({
  data,
  interval,
  dateLabel,
  providerPerKwhCharge,
  providerSpotCoefficient,
  includeTaxes,
  currentTime,
  regionId,
}: PriceSummaryCardsProps) {
  const pricePoints = useMemo(() => {
    const energyTaxRate = COUNTRY_CONFIG.getEnergyTaxRate(
      currentTime,
      regionId ?? undefined
    );

    return Object.entries(data)
      .sort(([firstSlot], [secondSlot]) => firstSlot.localeCompare(secondSlot))
      .map(([slot, basePrice]): PricePoint => ({
        slot,
        timeRange: formatTimeRange(slot, interval),
        price: computePriceBreakdown({
          spotPrice: basePrice,
          perKwhMarkup: providerPerKwhCharge,
          spotCoefficient: providerSpotCoefficient,
          energyTaxRate,
          vatRate: COUNTRY_CONFIG.vatRate,
          includeTaxes,
        }).total,
      }));
  }, [
    currentTime,
    data,
    includeTaxes,
    interval,
    providerPerKwhCharge,
    providerSpotCoefficient,
    regionId,
  ]);

  if (pricePoints.length === 0) return null;

  const currentTimeSlot = getCurrentTimeSlot(interval, currentTime);
  const isToday =
    pricePoints[0].slot.slice(0, 10) === currentTimeSlot.slice(0, 10);
  const current = isToday
    ? pricePoints.find((point) =>
        interval === "60m"
          ? point.slot.startsWith(currentTimeSlot)
          : point.slot === currentTimeSlot
      )
    : undefined;
  const cheapestThreeHourWindow = isToday
    ? undefined
    : findCheapestThreeHourWindow(pricePoints, interval);
  const cheapest = pricePoints.reduce((lowest, point) =>
    point.price < lowest.price ? point : lowest
  );
  const mostExpensive = pricePoints.reduce((highest, point) =>
    point.price > highest.price ? point : highest
  );
  const average =
    pricePoints.reduce((total, point) => total + point.price, 0) /
    pricePoints.length;

  return (
    <section
      className="mb-8"
      aria-label={STRINGS.summary.sectionLabel(dateLabel, isToday)}
    >
      <MobilePriceOverview
        current={current}
        cheapest={cheapest}
        cheapestThreeHourWindow={cheapestThreeHourWindow}
        isToday={isToday}
        mostExpensive={mostExpensive}
        average={average}
        dateLabel={dateLabel}
      />

      <div
        className={cn(
          "hidden gap-4 md:grid",
          desktopGridColumns(isToday)
        )}
        data-testid="desktop-price-statistics"
      >
        {isToday && (
          <StatisticCard
            icon={Clock3}
            iconClassName="text-primary"
            iconWrapperClassName="bg-cyan-50 dark:bg-cyan-950"
            label={STRINGS.summary.currentPrice}
            primaryValue={
              current ? formatPrice(current.price) : STRINGS.summary.notAvailable
            }
            primaryValueAriaLabel={
              current ? undefined : STRINGS.summary.noCurrentPrice
            }
            secondaryValue={
              current
                ? STRINGS.summary.perKwh
                : STRINGS.summary.forThisDay(dateLabel)
            }
          />
        )}

        <StatisticCard
          icon={TrendingDown}
          iconClassName="text-positive"
          iconWrapperClassName="bg-emerald-50 dark:bg-emerald-950"
          label={STRINGS.summary.cheapest(interval)}
          primaryValue={cheapest.timeRange}
          secondaryValue={`${formatPrice(cheapest.price)} ${STRINGS.summary.perKwh}`}
        />

        <StatisticCard
          icon={TrendingUp}
          iconClassName="text-destructive"
          iconWrapperClassName="bg-rose-50 dark:bg-rose-950"
          label={STRINGS.summary.mostExpensive(interval)}
          primaryValue={mostExpensive.timeRange}
          secondaryValue={`${formatPrice(mostExpensive.price)} ${STRINGS.summary.perKwh}`}
        />

        <StatisticCard
          icon={Zap}
          iconClassName="text-primary"
          iconWrapperClassName="bg-cyan-50 dark:bg-cyan-950"
          label={STRINGS.summary.averageOn(dateLabel, isToday)}
          primaryValue={formatPrice(average)}
          secondaryValue={STRINGS.summary.perKwh}
        />
      </div>
    </section>
  );
}

export function PriceSummaryCardsSkeleton({
  showCurrentPrice = true,
}: {
  showCurrentPrice?: boolean;
}) {
  return (
    <section
      className="mb-8"
      aria-hidden="true"
      data-testid="price-summary-skeleton"
    >
      <Card className="md:hidden">
        <CardContent className="p-0">
          <div className="flex items-start justify-between gap-4 px-4 pb-4 pt-4 xs:px-5 xs:pt-5">
            <div>
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-2 h-7.5 w-32" />
              <Skeleton className="mt-2 h-5 w-14" />
            </div>
            <Skeleton className="h-5 w-24" />
          </div>
          <div className="border-t border-border px-5 py-4">
            <Skeleton className="h-4 w-36" />
            <div className="mt-2 flex items-center justify-between gap-4">
              <div>
                <Skeleton className="h-7 w-32" />
                <Skeleton className="mt-1 h-4 w-40" />
              </div>
              <Skeleton className="h-10 w-10 rounded-full" />
            </div>
          </div>
          <div className="grid grid-cols-3 border-t border-border">
            {["items-start", "items-center border-x border-border", "items-end"].map(
              (alignment) => (
                <div
                  key={alignment}
                  className={cn("flex flex-col px-3 py-3 xs:px-4", alignment)}
                >
                  <Skeleton className="h-3.5 w-12 xs:h-4" />
                  <Skeleton className="mt-1 h-5 w-14 xs:h-6" />
                  <Skeleton className="mt-1 h-3.5 w-16 xs:h-4" />
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>

      <div
        className={cn(
          "hidden gap-4 md:grid",
          desktopGridColumns(showCurrentPrice)
        )}
      >
        {Array.from({ length: showCurrentPrice ? 4 : 3 }, (_, index) => (
          <Card key={index} className="min-w-0">
            <CardContent className="flex h-full flex-col p-3 xs:p-4 lg:p-5 xl:p-6">
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 shrink-0 rounded-lg xs:h-9 xs:w-9" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="mt-3 h-6 w-28 lg:h-5 xl:h-6" />
              <Skeleton className="mt-2 h-4 w-20 xs:h-5" />
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
